import React, { createContext, useEffect, useState, useCallback } from "react";
import type { User } from "firebase/auth";
import { signOut as firebaseSignOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../lib/firebase";
import {
  googleProvider,
  appleProvider,
  microsoftProvider,
} from "../lib/firebase-auth-providers";
import { signInWithPopupWithTimeout } from "../utils/oauthTimeout";
import { createUserProfile, getUserProfile } from "../services/userService";
import { recordPrivacyAcceptance } from "../services/userService";
import { getOnboardingStatus } from "../services/onboardingService";
import type { UserProfile, AuthUser } from "../types/user";
import { createErrorHandler } from "../utils/errorHandler";
import type { AppError } from "../types/errors";
import { mapFirebaseError } from "../types/errors";
import { createLogger } from "../utils/logger";

interface AuthContextType {
  user: AuthUser | null;
  userProfile: UserProfile | undefined;
  loading: boolean;
  onboardingWelcomeViewed: boolean;
  onboardingCompleted: boolean;
  privacyAccepted: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  updateOnboardingWelcomeViewed: (viewed: boolean) => void;
  updateOnboardingCompleted: (completed: boolean) => void;
  acceptPrivacy: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingWelcomeViewed, setOnboardingWelcomeViewed] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Create logger instance for this module - use useMemo to ensure stable reference
  // Note: stable reference means we don't need to include it in useEffect dependencies
  const authLogger = React.useMemo(
    () => createLogger({ prefix: "AuthContext" }),
    []
  );

  // Create error handler with injected dependencies
  // Empty deps array is correct - setError from useState is guaranteed stable by React
  const handleError = useCallback((error: AppError) => {
    const handler = createErrorHandler({
      setError: (msg) => setError(msg), // setError from useState is stable
    });
    handler(error);
  }, []); // Empty deps OK - setError is guaranteed stable by React

  // Load user profile when user changes
  useEffect(() => {
    const loadUserProfile = async (currentUser: User) => {
      setLoading(true);

      // Run both Firestore reads concurrently — sequential reads doubled latency
      // (each read takes 5–8 s on CI, so sequential = 10–16 s vs parallel ≈ 8 s max).
      const [result, onboardingResult] = await Promise.all([
        getUserProfile({ userId: currentUser.uid }),
        getOnboardingStatus(currentUser.uid),
      ]);

      if (!result.ok) {
        // Profile loading failed - log error for debugging
        authLogger.error("Error loading user profile", {
          error: result.error,
          userId: currentUser.uid,
        });

        // Graceful error handling: Sign out user to prevent stuck error state
        // This prevents persistent errors (e.g., Firestore offline errors) from keeping user in broken state
        authLogger.warn("Signing out user due to profile loading failure", {
          userId: currentUser.uid,
        });

        try {
          await firebaseSignOut(auth);
          setUserProfile(undefined);
          setError(null); // Clear error after sign out
        } catch (signOutError) {
          authLogger.error("Error during automatic sign out", {
            error: signOutError,
            userId: currentUser.uid,
          });
          // Still set error for user visibility even if sign out fails
          handleError(result.error);
        }

        setLoading(false);
        return;
      }

      if (result.value) {
        setUserProfile(result.value);
        setPrivacyAccepted(!!result.value.privacyAcceptedAt);

        // Apply onboarding status fetched in parallel above
        if (onboardingResult.ok && onboardingResult.value) {
          setOnboardingWelcomeViewed(onboardingResult.value.welcomeViewed);
          setOnboardingCompleted(onboardingResult.value.completed);
        } else {
          // Not found in Firestore - fresh user
          setOnboardingWelcomeViewed(false);
          setOnboardingCompleted(false);
        }
      } else {
        setUserProfile(undefined);
        setPrivacyAccepted(false);
        setOnboardingWelcomeViewed(false);
        setOnboardingCompleted(false);
      }
      setLoading(false);
    };

    if (user) {
      loadUserProfile(user);
    } else {
      setUserProfile(undefined);
      setOnboardingWelcomeViewed(false);
      setPrivacyAccepted(false);
      // No need to set loading here - onAuthStateChanged handles it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authLogger stable (useMemo with empty deps), handleError stable (useState callback)
  }, [user]);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        // No user: auth is fully resolved — clear loading immediately.
        // For authenticated users, loading is cleared by loadUserProfile
        // (above) only after the Firestore profile + onboarding reads finish.
        // This prevents a brief render where loading=false but
        // onboardingCompleted=false, which would cause ProtectedRoute to
        // redirect to /onboarding/welcome before the Firestore read completes.
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const clearError = () => {
    setError(null);
  };

  const signInWithGoogle = async () => {
    setError(null);

    try {
      const result = await signInWithPopupWithTimeout(auth, googleProvider);
      const currentUser = result.user;

      // Parse display name into first and last name
      const displayName = currentUser.displayName || "User";
      const nameParts = displayName.trim().split(/\s+/);
      const firstName = nameParts[0] || "User";
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      // Create or update user profile (createUserProfile handles checking if user exists)
      const profileResult = await createUserProfile({
        userId: currentUser.uid,
        email: currentUser.email || "",
        firstName,
        lastName,
        provider: "google",
        providerUserId: result.user.uid,
        avatarUrl: currentUser.photoURL || undefined,
      });

      if (!profileResult.ok) {
        authLogger.error(
          "Error creating/updating user profile after Google sign-in",
          {
            error: profileResult.error,
            userId: currentUser.uid,
          }
        );
        handleError(profileResult.error);
        throw new Error("Failed to create/update profile");
      }

      // Use the returned profile directly to avoid duplicate logic
      setUserProfile(profileResult.value);
    } catch (err) {
      authLogger.error("Google sign-in error", { error: err });
      // Map Firebase error to AppError and handle it
      const appError = mapFirebaseError(err);
      handleError(appError);
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
      setUserProfile(undefined);
      setOnboardingCompleted(false);
      setOnboardingWelcomeViewed(false);
      setPrivacyAccepted(false);
      // Clear persisted session data so the next user starts fresh
      localStorage.removeItem("retirement-storage");
      localStorage.removeItem("feedbackRateLimits");
    } catch (err) {
      // Map Firebase error to AppError and handle it
      const appError = mapFirebaseError(err);
      handleError(appError);
      throw err;
    }
  };

  const updateOnboardingWelcomeViewed = (viewed: boolean) => {
    setOnboardingWelcomeViewed(viewed);
  };

  const updateOnboardingCompleted = (completed: boolean) => {
    setOnboardingCompleted(completed);
  };

  const acceptPrivacy = useCallback(async () => {
    if (!user) return;
    const result = await recordPrivacyAcceptance(user.uid);
    if (result.ok) {
      setPrivacyAccepted(true);
    } else {
      handleError(result.error);
    }
  }, [user, handleError]);

  const signInWithApple = async () => {
    setError(null);

    try {
      const result = await signInWithPopupWithTimeout(auth, appleProvider);
      const currentUser = result.user;

      // Parse display name into first and last name
      const displayName = currentUser.displayName || "User";
      const nameParts = displayName.trim().split(/\s+/);
      const firstName = nameParts[0] || "User";
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      // Create or update user profile
      const profileResult = await createUserProfile({
        userId: currentUser.uid,
        email: currentUser.email || "",
        firstName,
        lastName,
        provider: "apple",
        providerUserId: result.user.uid,
        avatarUrl: currentUser.photoURL || undefined,
      });

      if (!profileResult.ok) {
        authLogger.error(
          "Error creating/updating user profile after Apple sign-in",
          {
            error: profileResult.error,
            userId: currentUser.uid,
          }
        );
        handleError(profileResult.error);
        throw new Error("Failed to create/update profile");
      }

      setUserProfile(profileResult.value);
    } catch (err) {
      authLogger.error("Apple sign-in error", { error: err });
      const appError = mapFirebaseError(err);
      handleError(appError);
      throw err;
    }
  };

  const signInWithMicrosoft = async () => {
    setError(null);

    try {
      const result = await signInWithPopupWithTimeout(auth, microsoftProvider);
      const currentUser = result.user;

      // Parse display name into first and last name
      const displayName = currentUser.displayName || "User";
      const nameParts = displayName.trim().split(/\s+/);
      const firstName = nameParts[0] || "User";
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      // Create or update user profile
      const profileResult = await createUserProfile({
        userId: currentUser.uid,
        email: currentUser.email || "",
        firstName,
        lastName,
        provider: "microsoft",
        providerUserId: result.user.uid,
        avatarUrl: currentUser.photoURL || undefined,
      });

      if (!profileResult.ok) {
        authLogger.error(
          "Error creating/updating user profile after Microsoft sign-in",
          {
            error: profileResult.error,
            userId: currentUser.uid,
          }
        );
        handleError(profileResult.error);
        throw new Error("Failed to create/update profile");
      }

      setUserProfile(profileResult.value);
    } catch (err) {
      authLogger.error("Microsoft sign-in error", { error: err });
      const appError = mapFirebaseError(err);
      handleError(appError);
      throw err;
    }
  };

  const value: AuthContextType = {
    user: user ? { userId: user.uid, email: user.email || "" } : null,
    userProfile,
    loading,
    onboardingWelcomeViewed,
    onboardingCompleted,
    privacyAccepted,
    signInWithGoogle,
    signInWithApple,
    signInWithMicrosoft,
    signOut,
    error,
    clearError,
    updateOnboardingWelcomeViewed,
    updateOnboardingCompleted,
    acceptPrivacy,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
